import * as Sentry from "@sentry/react";
import { sorted } from "helpers";
import {
  sentryVideoPlaybackSampleRate,
  VIDEO_PLAYBACK_SPAN_OP,
} from "../constants";

export const REPORT_INTERVAL_MS = 300_000;
const SLOW_SEEK_MS = 1_000;
const SLOW_FIRST_FRAME_MS = [2_000, 5_000] as const;

export interface CameraAttributes {
  tenant: string | undefined;
  "video.page": string;
  "video.surface": "dialog" | "page";
  "video.viewer_timezone": string;
}

export const REPLAY_BUCKET_SECONDS = [5, 10, 30, 120] as const;
export type ReplayBucketSeconds = (typeof REPLAY_BUCKET_SECONDS)[number];

export interface CameraPlayback {
  camera: string | undefined;
  quality: string | undefined;
  attributes: CameraAttributes;
  sharedHoldMs: number;
  sharedHoldCount: number;
  holdCausedMs: number;
  seekMs: number[];
  seekAbandonedCount: number;
  seekBackwardCount: number;
  seekBackwardUnplayedCount: number;
  seekBackwardReplaySeconds: Record<ReplayBucketSeconds, number>;
  seekPendingMs: number | null;
  firstFrameMs: number[];
  startupWaitMs: number[];
  startupPendingMs: number | null;
  segmentCount: number;
  segmentBytes: number;
  segmentLoadMs: number;
  segmentFirstDataMs: number;
}

interface PageDownload {
  start: number;
  end: number;
  bytes: number;
}

let pageDownloads: PageDownload[] = [];

export const recordPageDownload = (download: PageDownload) => {
  const keepAfter = download.end - 2 * REPORT_INTERVAL_MS;
  pageDownloads = [
    ...pageDownloads.filter(({ end }) => end >= keepAfter),
    download,
  ];
};

const pageDownloadMbps = (from: number, to: number): number | null => {
  const clipped = sorted(
    pageDownloads.flatMap(({ start, end, bytes }) => {
      const clippedStart = Math.max(start, from);
      const clippedEnd = Math.min(end, to);
      if (clippedEnd <= clippedStart) return [];
      return [
        {
          start: clippedStart,
          end: clippedEnd,
          bytes: (bytes * (clippedEnd - clippedStart)) / (end - start),
        },
      ];
    }),
    ({ start }) => start
  );
  let bytes = 0;
  let busyMs = 0;
  let coveredUntil = -Infinity;
  for (const download of clipped) {
    bytes += download.bytes;
    busyMs += Math.max(
      0,
      download.end - Math.max(download.start, coveredUntil)
    );
    coveredUntil = Math.max(coveredUntil, download.end);
  }
  return busyMs > 0 ? (bytes * 8) / busyMs / 1000 : null;
};

interface NetworkInformation {
  downlink?: number;
  rtt?: number;
  effectiveType?: string;
}

const browserConnectionAttributes = () => {
  const connection = (navigator as { connection?: NetworkInformation })
    .connection;
  if (!connection) return {};
  return {
    "video.browser_downlink_mbps": connection.downlink,
    "video.browser_rtt_ms": connection.rtt,
    "video.browser_effective_type": connection.effectiveType,
  };
};

const sum = (values: number[]) =>
  values.reduce((total, value) => total + value, 0);
const max = (values: number[]) => (values.length ? Math.max(...values) : 0);
const countAtLeast = (values: number[], threshold: number) =>
  values.filter((value) => value >= threshold).length;

const hasActivity = (playback: CameraPlayback) =>
  playback.sharedHoldMs > 0 ||
  playback.seekMs.length > 0 ||
  playback.seekAbandonedCount > 0 ||
  playback.seekPendingMs !== null ||
  playback.firstFrameMs.length > 0 ||
  playback.startupWaitMs.length > 0 ||
  playback.startupPendingMs !== null ||
  playback.segmentCount > 0;

const totalPerCamera = (
  playbacks: CameraPlayback[],
  value: (playback: CameraPlayback) => number
) =>
  playbacks.reduce(
    (totals, playback) =>
      totals.set(
        playback.camera,
        (totals.get(playback.camera) ?? 0) + value(playback)
      ),
    new Map<string | undefined, number>()
  );

const gridWideTotal = (
  playbacks: CameraPlayback[],
  value: (playback: CameraPlayback) => number
) => max([...totalPerCamera(playbacks, value).values()]);

const gridAttributes = (playbacks: CameraPlayback[]) => {
  const seekMs = playbacks.flatMap(({ seekMs }) => seekMs);
  const firstFrameMs = playbacks.flatMap(({ firstFrameMs }) => firstFrameMs);
  const startupWaitMs = playbacks.flatMap(({ startupWaitMs }) => startupWaitMs);
  const seekPendingMs = playbacks.flatMap(({ seekPendingMs }) =>
    seekPendingMs === null ? [] : [seekPendingMs]
  );
  const startupPendingMs = playbacks.flatMap(({ startupPendingMs }) =>
    startupPendingMs === null ? [] : [startupPendingMs]
  );
  const holdCausedMs = totalPerCamera(playbacks, (p) => p.holdCausedMs);
  const [holdCausedByCamera] = sorted(
    [...holdCausedMs].filter(([, ms]) => ms > 0),
    ([, ms]) => -ms
  );
  const cameras = sorted(
    [...new Set(playbacks.flatMap(({ camera }) => camera ?? []))],
    (camera) => camera
  );
  const total = (value: (playback: CameraPlayback) => number) =>
    sum(playbacks.map(value));
  return {
    ...playbacks[0]?.attributes,
    "video.cameras": cameras.join(","),
    "video.camera_count": cameras.length,
    "video.camera_session_count": playbacks.length,
    "video.shared_hold_ms": gridWideTotal(playbacks, (p) => p.sharedHoldMs),
    "video.shared_hold_count": gridWideTotal(
      playbacks,
      (p) => p.sharedHoldCount
    ),
    "video.hold_caused_ms": total((p) => p.holdCausedMs),
    ...(holdCausedByCamera?.[0] !== undefined && {
      "video.hold_caused_by_camera": holdCausedByCamera[0],
    }),
    "video.seek_count": seekMs.length,
    "video.seek_ms": sum(seekMs),
    "video.seek_max_ms": max(seekMs),
    "video.seek_slow_count": countAtLeast(seekMs, SLOW_SEEK_MS),
    "video.seek_abandoned_count": total((p) => p.seekAbandonedCount),
    "video.seek_backward_count": total((p) => p.seekBackwardCount),
    "video.seek_backward_unplayed_count": total(
      (p) => p.seekBackwardUnplayedCount
    ),
    ...Object.fromEntries(
      REPLAY_BUCKET_SECONDS.map((seconds) => [
        `video.seek_backward_replay_${seconds}s_count`,
        total((p) => p.seekBackwardReplaySeconds[seconds]),
      ])
    ),
    "video.seek_pending_count": seekPendingMs.length,
    "video.seek_pending_max_ms": max(seekPendingMs),
    "video.first_frame_count": firstFrameMs.length,
    "video.first_frame_total_ms": sum(firstFrameMs),
    "video.first_frame_max_ms": max(firstFrameMs),
    "video.first_frame_over_2s_count": countAtLeast(
      firstFrameMs,
      SLOW_FIRST_FRAME_MS[0]
    ),
    "video.first_frame_over_5s_count": countAtLeast(
      firstFrameMs,
      SLOW_FIRST_FRAME_MS[1]
    ),
    "video.startup_abandoned_count": startupWaitMs.length,
    "video.startup_wait_total_ms": sum(startupWaitMs),
    "video.startup_wait_max_ms": max(startupWaitMs),
    "video.startup_pending_count": startupPendingMs.length,
    "video.startup_pending_max_ms": max(startupPendingMs),
    "video.segment_count": total((p) => p.segmentCount),
    "video.segment_bytes": total((p) => p.segmentBytes),
    "video.segment_load_ms": total((p) => p.segmentLoadMs),
    "video.segment_first_data_ms": total((p) => p.segmentFirstDataMs),
  };
};

export interface VideoPlaybackReport {
  register: (collect: () => CameraPlayback) => {
    release: (final: CameraPlayback) => void;
  };
  setPlaybackRate: (rate: number) => void;
  start: () => void;
  close: () => void;
}

export const createVideoPlaybackReport = (): VideoPlaybackReport => {
  const collectors = new Set<() => CameraPlayback>();
  let released: CameraPlayback[] = [];
  let startedAt = performance.now();
  let playbackRate: number | undefined;
  let sendScheduled = false;
  let stop: (() => void) | null = null;

  const send = () => {
    sendScheduled = false;
    const now = performance.now();
    const playbacks = [
      ...released,
      ...[...collectors].map((collect) => collect()),
    ];
    released = [];
    const pageMbps = pageDownloadMbps(startedAt, now);
    for (const quality of new Set(playbacks.map(({ quality }) => quality))) {
      const qualityPlaybacks = playbacks.filter(
        (playback) => playback.quality === quality
      );
      if (!qualityPlaybacks.some(hasActivity) || now <= startedAt) continue;
      const span = Sentry.startInactiveSpan({
        name: "Video playback",
        op: VIDEO_PLAYBACK_SPAN_OP,
        parentSpan: null,
        startTime: (performance.timeOrigin + startedAt) / 1000,
        attributes: {
          ...gridAttributes(qualityPlaybacks),
          ...browserConnectionAttributes(),
          ...(pageMbps !== null && { "video.page_download_mbps": pageMbps }),
          ...(playbackRate !== undefined && {
            "video.playback_rate": playbackRate,
          }),
          ...(quality !== undefined && { "video.quality": quality }),
          "video.sample_rate": sentryVideoPlaybackSampleRate,
        },
      });
      span.end((performance.timeOrigin + now) / 1000);
    }
    startedAt = now;
  };

  // Deferred so each camera's visibility and unmount handlers run first.
  const sendSoon = () => {
    if (sendScheduled) return;
    sendScheduled = true;
    queueMicrotask(send);
  };

  return {
    register: (collect) => {
      collectors.add(collect);
      return {
        release: (final) => {
          collectors.delete(collect);
          released.push(final);
        },
      };
    },
    setPlaybackRate: (rate) => {
      if (playbackRate !== undefined && rate !== playbackRate) send();
      playbackRate = rate;
    },
    start: () => {
      if (stop) return;
      startedAt = performance.now();
      const controller = new AbortController();
      const options = { signal: controller.signal };
      document.addEventListener(
        "visibilitychange",
        () => {
          if (document.hidden) sendSoon();
          else startedAt = performance.now();
        },
        options
      );
      window.addEventListener("pagehide", sendSoon, options);
      const interval = setInterval(() => {
        if (!document.hidden) send();
      }, REPORT_INTERVAL_MS);
      stop = () => {
        clearInterval(interval);
        controller.abort();
      };
    },
    close: () => {
      stop?.();
      stop = null;
      sendSoon();
    },
  };
};
